import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Code,
  X,
  Loader2,
  RotateCcw,
  Lock,
  Users,
  UserCheck,
  Download,
  Search,
  LayoutGrid,
  List,
  Heart,
  Trash2,
  Clock3,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import {
  listMyApps,
  listDeletedApps,
  listSharedApps,
  listCategories,
  deleteApp,
  restoreApp,
  startApp,
  stopApp,
  rebuildApp,
  listAppShares,
  shareApp,
  removeAppShare,
  shareWithPlatform,
  unshareFromPlatform,
  favoriteApp,
  unfavoriteApp,
  type App,
  type DeletedApp,
  type SharedApp,
  type AppShare,
} from '../lib/api'
import CreateAppModal from '../components/CreateAppModal'
import ImportProjectModal from '../components/ImportProjectModal'
import { buildCategoryConfig, type CategoryStyle } from '../lib/categoryColors'
import { withMountBase } from '../lib/mountBase'
import { formatRelativeTime } from '../lib/format'
import AppActions from '../components/AppActions'
import { VisitorBadge } from '../components/VisitorBadge'
import { Switch } from '../components/ui/Switch'
import { HelpLink } from '../components/HelpLink'
import {
  BrowseGridCard,
  BrowseListCard,
  FeaturedIcon,
  PopularIcon,
  FavoriteButton,
  PermissionBadge,
} from '../components/BrowseCard'

const accessLevelConfig = {
  personal: { icon: Lock, label: 'Personal' },
  specific: { icon: UserCheck, label: 'Specific Users' },
  platform: { icon: Users, label: 'Platform' },
}

const runtimeConfig = {
  node: { label: 'Node.js', color: 'bg-green-500' },
  python: { label: 'Python', color: 'bg-blue-500' },
  static: { label: 'Static', color: 'bg-purple-500' },
}

const statusConfig = {
  running: { color: 'bg-green-500', label: 'Running' },
  stopped: { color: 'bg-gray-500', label: 'Stopped' },
  starting: { color: 'bg-yellow-500', label: 'Starting' },
  error: { color: 'bg-red-500', label: 'Error' },
}

let categoryConfig: Record<string, CategoryStyle> = {}



export default function MyApps() {
  const [apps, setApps] = useState<App[]>([])
  const [deletedApps, setDeletedApps] = useState<DeletedApp[]>([])
  const [sharedApps, setSharedApps] = useState<SharedApp[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [showShare, setShowShare] = useState<App | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [workspaceLaunchMessage, setWorkspaceLaunchMessage] = useState<string | null>(null)

  // Tab, search, filter, sort, view state
  const [activeTab, setActiveTab] = useState<'my-apps' | 'shared'>(() => {
    const saved = localStorage.getItem('myapps-tab')
    return saved === 'shared' ? 'shared' : 'my-apps'
  })
  const [search, setSearch] = useState('')
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [accessFilter, setAccessFilter] = useState('all')
  const [permissionFilter, setPermissionFilter] = useState('all')
  const [sortBy, setSortBy] = useState('recent')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return (localStorage.getItem('myapps-view') as 'grid' | 'list') || 'grid'
  })
  const [restoringAppNames, setRestoringAppNames] = useState<string[]>([])
  const [showDeletedApps, setShowDeletedApps] = useState(false)

  useEffect(() => {
    loadApps()
  }, [])

  useEffect(() => {
    localStorage.setItem('myapps-view', viewMode)
  }, [viewMode])

  useEffect(() => {
    localStorage.setItem('myapps-tab', activeTab)
  }, [activeTab])

  useEffect(() => {
    if (deletedApps.length === 0) {
      setShowDeletedApps(false)
    }
  }, [deletedApps.length])

  async function loadApps() {
    try {
      const [myApps, deleted, shared] = await Promise.all([
        listMyApps(),
        listDeletedApps(),
        listSharedApps(),
      ])
      setApps(myApps)
      setDeletedApps(deleted)
      setSharedApps(shared)
      try {
        const cats = await listCategories()
        categoryConfig = buildCategoryConfig(cats)
      } catch {
        categoryConfig = {}
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load apps')
    } finally {
      setLoading(false)
    }
  }

  const filteredApps = useMemo(() => {
    return apps
      .filter(app => {
        const matchesSearch = search === '' ||
          (app.display_name || app.name).toLowerCase().includes(search.toLowerCase()) ||
          (app.description || '').toLowerCase().includes(search.toLowerCase())
        const matchesStatus = statusFilter === 'all' || app.status === statusFilter
        const matchesAccess = accessFilter === 'all' || app.access_level === accessFilter
        const matchesFav = !showFavoritesOnly || app.is_favorited
        return matchesSearch && matchesStatus && matchesAccess && matchesFav
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return (a.display_name || a.name).localeCompare(b.display_name || b.name)
          case 'name-desc':
            return (b.display_name || b.name).localeCompare(a.display_name || a.name)
          case 'visitors':
            return (b.visit_count_30d || 0) - (a.visit_count_30d || 0)
          default: // 'recent'
            return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime()
        }
      })
  }, [apps, search, statusFilter, accessFilter, sortBy, showFavoritesOnly])

  const filteredSharedApps = useMemo(() => {
    return sharedApps
      .filter(app => {
        const matchesSearch = search === '' ||
          (app.display_name || app.name).toLowerCase().includes(search.toLowerCase()) ||
          (app.description || '').toLowerCase().includes(search.toLowerCase()) ||
          (app.owner_name || app.owner_email).toLowerCase().includes(search.toLowerCase())
        const matchesPerm = permissionFilter === 'all' ||
          (permissionFilter === 'edit' ? (app.my_permission === 'edit' || app.my_permission === 'admin') : app.my_permission === 'view')
        const matchesFav = !showFavoritesOnly || app.is_favorited
        return matchesSearch && matchesPerm && matchesFav
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return (a.display_name || a.name).localeCompare(b.display_name || b.name)
          case 'name-desc':
            return (b.display_name || b.name).localeCompare(a.display_name || a.name)
          case 'visitors':
            return (b.visit_count_30d || 0) - (a.visit_count_30d || 0)
          default: // 'recent'
            return new Date(b.updated_at || b.created_at).getTime() -
              new Date(a.updated_at || a.created_at).getTime()
        }
      })
  }, [sharedApps, search, permissionFilter, sortBy, showFavoritesOnly])

  const filteredDeletedApps = useMemo(() => {
    return deletedApps.filter(app => {
      return search === '' ||
        (app.display_name || app.name).toLowerCase().includes(search.toLowerCase()) ||
        (app.description || '').toLowerCase().includes(search.toLowerCase())
    })
  }, [deletedApps, search])

  const hasActiveFilters = search !== '' || statusFilter !== 'all' || accessFilter !== 'all' || permissionFilter !== 'all' || showFavoritesOnly
  const deletedSearchMatches = activeTab === 'my-apps' ? filteredDeletedApps.length : 0

  function clearFilters() {
    setSearch('')
    setStatusFilter('all')
    setAccessFilter('all')
    setPermissionFilter('all')
    setShowFavoritesOnly(false)
  }

  async function handleDelete(app: App) {
    setError(null)
    try {
      const deleted = await deleteApp(app.name)
      setApps(prev => prev.filter(candidate => candidate.name !== deleted.name))
      setDeletedApps(prev => [deleted, ...prev.filter(candidate => candidate.name !== deleted.name)])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete app')
      throw err
    }
  }

  async function handleRestore(app: DeletedApp) {
    const previousDeletedApps = deletedApps
    setError(null)
    setRestoringAppNames(prev => [...prev, app.name])
    setDeletedApps(prev => prev.filter(candidate => candidate.name !== app.name))
    try {
      const restored = await restoreApp(app.name)
      setApps(prev => [restored, ...prev.filter(candidate => candidate.name !== restored.name)])
    } catch (err) {
      setDeletedApps(previousDeletedApps)
      setError(err instanceof Error ? err.message : 'Failed to restore app')
    } finally {
      setRestoringAppNames(prev => prev.filter(name => name !== app.name))
    }
  }

  async function handleStart(app: App) {
    try {
      await startApp(app.name)
      await loadApps()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start app')
    }
  }

  async function handleStop(app: App) {
    try {
      await stopApp(app.name)
      await loadApps()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop app')
    }
  }

  async function handleRebuild(app: App) {
    try {
      await rebuildApp(app.name)
      await loadApps()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rebuild app')
    }
  }

  async function handleToggleFavorite(app: App | SharedApp) {
    const wasFavorited = app.is_favorited
    // Optimistic update on owned apps
    setApps(prev => prev.map(a =>
      a.name === app.name ? { ...a, is_favorited: !wasFavorited } : a
    ))
    try {
      if (wasFavorited) {
        await unfavoriteApp(app.name)
      } else {
        await favoriteApp(app.name)
      }
    } catch {
      // Revert on error
      setApps(prev => prev.map(a =>
        a.name === app.name ? { ...a, is_favorited: wasFavorited } : a
      ))
    }
  }

  async function handleToggleSharedFavorite(app: App | SharedApp) {
    const wasFavorited = app.is_favorited
    // Optimistic update on shared apps
    setSharedApps(prev => prev.map(a =>
      a.name === app.name ? { ...a, is_favorited: !wasFavorited } : a
    ))
    try {
      if (wasFavorited) {
        await unfavoriteApp(app.name)
      } else {
        await favoriteApp(app.name)
      }
    } catch {
      // Revert on error
      setSharedApps(prev => prev.map(a =>
        a.name === app.name ? { ...a, is_favorited: wasFavorited } : a
      ))
    }
  }

  function renderSharedActions(app: SharedApp) {
    const permission = app.my_permission as 'view' | 'edit' | 'admin'
    const canEdit = permission === 'edit' || permission === 'admin'
    return (
      <AppActions
        app={app}
        permission={permission}
        onStart={async () => {
          await startApp(app.name)
          await loadApps()
        }}
        onStop={async () => {
          await stopApp(app.name)
          await loadApps()
        }}
        onRebuild={async () => {
          await rebuildApp(app.name)
          await loadApps()
        }}
        onShare={canEdit ? () => setShowShare(app) : undefined}
        size="sm"
      />
    )
  }

  function launchWorkspaceWithFeedback(targetUrl: string, message: string) {
    setWorkspaceLaunchMessage(message)
    window.setTimeout(() => {
      window.location.href = targetUrl
    }, 50)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">My Apps</h1>
          <p className="text-muted-foreground">
            Create and manage your deployed applications.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-card border border-border text-foreground rounded-lg hover:bg-secondary transition-colors"
          >
            <Download className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create App
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span>{error}</span>
            <HelpLink section="apps">View help</HelpLink>
          </div>
          <button onClick={() => setError(null)} className="p-1 hover:bg-destructive/20 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Create App Modal */}
      {showCreate && (
        <CreateAppModal
          onClose={() => setShowCreate(false)}
          onCreated={(app) => {
            setShowCreate(false)
            const workspacePath = withMountBase('/workspace', window.location.pathname)
            const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
            const appUrl = `${window.location.origin}${appPath}`
            const target = `${workspacePath}?app=${encodeURIComponent(app.name)}&url=${encodeURIComponent(appUrl)}`
            launchWorkspaceWithFeedback(
              target,
              `Preparing workspace for ${app.name}...`,
            )
          }}
        />
      )}

      {/* Import Project Modal */}
      {showImport && (
        <ImportProjectModal
          onClose={() => setShowImport(false)}
          onImported={(appName, importId) => {
            setShowImport(false)
            const workspacePath = withMountBase('/workspace', window.location.pathname)
            const appPath = withMountBase(`/a/${appName}/`, window.location.pathname)
            const appUrl = `${window.location.origin}${appPath}`
            const params = new URLSearchParams({
              app: appName,
              url: appUrl,
              import_id: String(importId),
            })
            launchWorkspaceWithFeedback(
              `${workspacePath}?${params.toString()}`,
              `Starting import for ${appName}...`,
            )
          }}
        />
      )}

      {/* Share Modal */}
      {showShare && (
        <ShareModal
          app={showShare}
          onClose={() => setShowShare(null)}
        />
      )}

      {/* Tabs */}
      {(apps.length > 0 || sharedApps.length > 0) && (
        <div className="flex items-center gap-1 mb-4 border-b border-border">
          {([
            { key: 'my-apps' as const, label: 'My Apps', count: apps.length },
            { key: 'shared' as const, label: 'Shared', count: sharedApps.length },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.key ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar: Search, Filters, Sort, View Toggle */}
      {(apps.length > 0 || sharedApps.length > 0) && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search apps..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {activeTab === 'my-apps' && (
            <>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-3 pr-8 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
              >
                <option value="all">All Status</option>
                <option value="running">Running</option>
                <option value="stopped">Stopped</option>
                <option value="starting">Starting</option>
                <option value="error">Error</option>
              </select>
              <select
                value={accessFilter}
                onChange={(e) => setAccessFilter(e.target.value)}
                className="pl-3 pr-8 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
              >
                <option value="all">All Access</option>
                <option value="personal">Personal</option>
                <option value="specific">Specific Users</option>
                <option value="platform">Platform</option>
              </select>
            </>
          )}

          {activeTab === 'shared' && (
            <select
              value={permissionFilter}
              onChange={(e) => setPermissionFilter(e.target.value)}
              className="pl-3 pr-8 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
            >
              <option value="all">All Permissions</option>
              <option value="edit">Editor</option>
              <option value="view">View Only</option>
            </select>
          )}

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="pl-3 pr-8 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
          >
            <option value="recent">Recently Updated</option>
            <option value="name-asc">Name A&rarr;Z</option>
            <option value="name-desc">Name Z&rarr;A</option>
            <option value="visitors">Most Visitors</option>
          </select>

          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors border ${
              showFavoritesOnly
                ? 'bg-yellow-500/15 border-yellow-500/30 text-yellow-600'
                : 'border-border text-muted-foreground hover:border-yellow-500/30 hover:text-yellow-600'
            }`}
          >
            <Heart className="w-3.5 h-3.5" fill={showFavoritesOnly ? 'currentColor' : 'none'} />
            Favorites
          </button>

          <div className="flex items-center border border-border rounded-lg overflow-hidden ml-auto">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
          <button onClick={clearFilters} className="text-primary hover:underline">
            Clear filters
          </button>
        </div>
      )}

      {/* Tab: My Apps */}
      {activeTab === 'my-apps' && (
        apps.length === 0 ? (
          deletedApps.length > 0 ? (
            <NoActiveAppsState onCreateClick={() => setShowCreate(true)} />
          ) : (
            <EmptyState onCreateClick={() => setShowCreate(true)} />
          )
        ) : filteredApps.length === 0 ? (
          <div className="text-center py-16 rounded-xl border border-dashed border-border">
            <Search className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No apps match your filters</h3>
            <p className="text-muted-foreground mb-4">
              {deletedSearchMatches > 0
                ? `${deletedSearchMatches} matching ${deletedSearchMatches === 1 ? 'app is' : 'apps are'} in Recently Deleted below.`
                : 'Try adjusting your search or filter criteria.'}
            </p>
            <button onClick={clearFilters} className="text-primary hover:underline text-sm">Clear all filters</button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredApps.map((app) => (
              <GridAppCard
                key={app.id}
                app={app}
                onStart={() => handleStart(app)}
                onStop={() => handleStop(app)}
                onRebuild={() => handleRebuild(app)}
                onShare={() => setShowShare(app)}
                onDelete={() => handleDelete(app)}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredApps.map((app) => (
              <AppCard
                key={app.id}
                app={app}
                onStart={() => handleStart(app)}
                onStop={() => handleStop(app)}
                onRebuild={() => handleRebuild(app)}
                onShare={() => setShowShare(app)}
                onDelete={() => handleDelete(app)}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>
        )
      )}

      {activeTab === 'my-apps' && deletedApps.length > 0 && (
        <RecentlyDeletedTray
          apps={filteredDeletedApps}
          totalCount={deletedApps.length}
          searchActive={search !== ''}
          restoringAppNames={restoringAppNames}
          onRestore={handleRestore}
          open={showDeletedApps}
          onToggle={() => setShowDeletedApps(open => !open)}
        />
      )}

      {/* Tab: Shared */}
      {activeTab === 'shared' && (
        filteredSharedApps.length === 0 ? (
          <div className="text-center py-16 rounded-xl border border-dashed border-border">
            <Users className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">
              {sharedApps.length === 0 ? 'No shared apps yet' : 'No apps match your filters'}
            </h3>
            <p className="text-muted-foreground">
              {sharedApps.length === 0
                ? 'When someone shares an app with you, it will appear here.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredSharedApps.map((app) => (
              <BrowseGridCard
                key={app.id}
                app={app}
                catCfg={app.category ? categoryConfig[app.category] : null}
                onToggleFavorite={handleToggleSharedFavorite}
                bottomBadges={<PermissionBadge permission={app.my_permission} />}
                actions={renderSharedActions(app)}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSharedApps.map((app) => (
              <BrowseListCard
                key={app.id}
                app={app}
                catCfg={app.category ? categoryConfig[app.category] : null}
                onToggleFavorite={handleToggleSharedFavorite}
                bottomBadges={<PermissionBadge permission={app.my_permission} />}
                actions={renderSharedActions(app)}
              />
            ))}
          </div>
        )
      )}

      {workspaceLaunchMessage && (
        <div className="fixed inset-0 z-[100] bg-background/90 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="rounded-xl border border-border bg-card px-6 py-5 shadow-lg flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <div className="text-sm">
              <p className="font-medium">Opening workspace...</p>
              <p className="text-muted-foreground">{workspaceLaunchMessage}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="text-center py-16 rounded-xl border border-dashed border-border">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
        <Code className="w-8 h-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No apps yet</h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        Create your first app to get started. Your app will be instantly
        available at a unique URL and ready to develop.
      </p>
      <button
        onClick={onCreateClick}
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Create Your First App
      </button>
    </div>
  )
}

function NoActiveAppsState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="text-center py-16 rounded-xl border border-dashed border-border">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
        <Trash2 className="w-8 h-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No active apps</h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        Your deleted apps stay restorable for 30 days in Recently Deleted below. Create a new app whenever you are ready.
      </p>
      <button
        onClick={onCreateClick}
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Create App
      </button>
    </div>
  )
}

function RecentlyDeletedTray({
  apps,
  totalCount,
  searchActive,
  restoringAppNames,
  onRestore,
  open,
  onToggle,
}: {
  apps: DeletedApp[]
  totalCount: number
  searchActive: boolean
  restoringAppNames: string[]
  onRestore: (app: DeletedApp) => Promise<void>
  open: boolean
  onToggle: () => void
}) {
  const toggleCount = searchActive ? apps.length : totalCount

  return (
    <div className="mt-6 border-t border-border/70 pt-4">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground"
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Trash2 className="w-4 h-4" />
        Recently Deleted
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
          {toggleCount}
        </span>
      </button>

      {open && (
        <div className="mt-4 rounded-xl border border-border bg-card p-4">
          <div className="mb-4">
            <h2 className="text-base font-semibold">Recently Deleted</h2>
            <p className="text-sm text-muted-foreground">
              Apps stay restorable for 30 days before permanent cleanup.
            </p>
          </div>

          {apps.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
              {searchActive ? 'No deleted apps match your search.' : 'No deleted apps are awaiting cleanup.'}
            </div>
          ) : (
            <div className="space-y-3">
              {apps.map((app) => {
                const restoring = restoringAppNames.includes(app.name)
                return (
                  <div
                    key={app.id}
                    className="flex flex-col gap-3 rounded-lg border border-border/80 bg-background/60 px-4 py-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{app.display_name || app.name}</div>
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        {app.description || 'No description'}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <Trash2 className="w-3.5 h-3.5" />
                          Deleted {formatRelativeTime(app.deleted_at)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Clock3 className="w-3.5 h-3.5" />
                          Purged {new Date(app.restore_deadline).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => onRestore(app)}
                      disabled={restoring}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors disabled:opacity-50"
                    >
                      {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                      Restore
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function GridAppCard({
  app,
  onStart,
  onStop,
  onRebuild,
  onShare,
  onDelete,
  onToggleFavorite,
}: {
  app: App
  onStart: () => Promise<void>
  onStop: () => Promise<void>
  onRebuild: () => Promise<void>
  onShare: () => void
  onDelete: () => Promise<void>
  onToggleFavorite: (app: App) => void
}) {
  const navigate = useNavigate()

  const AccessIcon = accessLevelConfig[app.access_level as keyof typeof accessLevelConfig]?.icon || Lock
  const accessLabel = accessLevelConfig[app.access_level as keyof typeof accessLevelConfig]?.label || 'Personal'
  const statusInfo = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.stopped
  const runtimeInfo = runtimeConfig[app.runtime as keyof typeof runtimeConfig] || { label: app.runtime, color: 'bg-gray-500' }
  const catCfg = app.category ? categoryConfig[app.category] : null

  return (
    <div
      className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-colors cursor-pointer flex flex-col"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a, [role="menu"]')) return
        navigate(`/apps/${app.name}`)
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${
              app.status === 'running'
                ? 'bg-green-500/20 text-green-500'
                : app.status === 'starting'
                ? 'bg-yellow-500/20 text-yellow-500'
                : app.status === 'error'
                ? 'bg-red-500/20 text-red-500'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.color}`} />
            {statusInfo.label}
          </span>
          {app.is_featured && <FeaturedIcon />}
          {!app.is_featured && (app.unique_visitors_30d || 0) >= 3 && <PopularIcon />}
          {catCfg && <span className={`text-[11px] ${catCfg.text} truncate`}>#{catCfg.label}</span>}
        </div>
        <div className="flex items-center gap-1">
          <FavoriteButton app={app} onToggle={onToggleFavorite} />
          <AppActions
            app={app}
            onStart={onStart}
            onStop={onStop}
            onRebuild={onRebuild}
            onShare={onShare}
            onDelete={onDelete}
            size="sm"
          />
        </div>
      </div>

      {app.url ? (
        <a
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold truncate mb-1 block hover:text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {app.display_name || app.name}
        </a>
      ) : (
        <h3 className="font-semibold truncate mb-1">{app.display_name || app.name}</h3>
      )}

      {app.description ? (
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{app.description}</p>
      ) : (
        <p className="text-sm text-muted-foreground italic mb-3">No description</p>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <AccessIcon className="w-3 h-3" />
            {accessLabel}
          </span>
          <span>Updated {formatRelativeTime(app.updated_at)}</span>
          <VisitorBadge count={app.visit_count_30d} accessLevel={app.access_level} />
        </div>
        <div className={`w-7 h-7 rounded-full ${runtimeInfo.color} bg-opacity-20 flex items-center justify-center flex-shrink-0`} title={runtimeInfo.label}>
          <span className="text-[10px] font-bold uppercase">{app.runtime.slice(0, 2)}</span>
        </div>
      </div>
    </div>
  )
}

function AppCard({
  app,
  onStart,
  onStop,
  onRebuild,
  onShare,
  onDelete,
  onToggleFavorite,
}: {
  app: App
  onStart: () => Promise<void>
  onStop: () => Promise<void>
  onRebuild: () => Promise<void>
  onShare: () => void
  onDelete: () => Promise<void>
  onToggleFavorite: (app: App) => void
}) {
  const navigate = useNavigate()

  const AccessIcon = accessLevelConfig[app.access_level as keyof typeof accessLevelConfig]?.icon || Lock
  const accessLabel = accessLevelConfig[app.access_level as keyof typeof accessLevelConfig]?.label || 'Personal'
  const statusInfo = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.stopped
  const runtimeInfo = runtimeConfig[app.runtime as keyof typeof runtimeConfig] || { label: app.runtime, color: 'bg-gray-500' }
  const catCfg = app.category ? categoryConfig[app.category] : null

  return (
    <div
      className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-colors cursor-pointer"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        navigate(`/apps/${app.name}`)
      }}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg ${runtimeInfo.color} bg-opacity-20 flex items-center justify-center flex-shrink-0`}>
          <span className="text-xs font-bold uppercase">{app.runtime.slice(0, 2)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {app.url ? (
              <a
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold truncate hover:text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {app.display_name || app.name}
              </a>
            ) : (
              <h3 className="font-semibold truncate">{app.display_name || app.name}</h3>
            )}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                app.status === 'running'
                  ? 'bg-green-500/20 text-green-500'
                  : app.status === 'starting'
                  ? 'bg-yellow-500/20 text-yellow-500'
                  : app.status === 'error'
                  ? 'bg-red-500/20 text-red-500'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.color}`} />
              {statusInfo.label}
            </span>
            {app.is_featured && <FeaturedIcon />}
            {!app.is_featured && (app.unique_visitors_30d || 0) >= 3 && <PopularIcon />}
            {catCfg && <span className={`text-[11px] ${catCfg.text}`}>#{catCfg.label}</span>}
          </div>
          {app.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{app.description}</p>
          )}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <AccessIcon className="w-3 h-3" />
              {accessLabel}
            </span>
            <span className="font-mono bg-secondary px-1.5 py-0.5 rounded">
              {app.url.replace('https://', '')}
            </span>
            <span>Updated {formatRelativeTime(app.updated_at)}</span>
            <VisitorBadge count={app.visit_count_30d} accessLevel={app.access_level} />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <FavoriteButton app={app} onToggle={onToggleFavorite} />
          <AppActions
            app={app}
            onStart={onStart}
            onStop={onStop}
            onRebuild={onRebuild}
            onShare={onShare}
            onDelete={onDelete}
            size="lg"
          />
        </div>
      </div>
    </div>
  )
}



function ShareModal({ app, onClose }: { app: App; onClose: () => void }) {
  const [shares, setShares] = useState<AppShare[]>([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [permission, setPermission] = useState('view')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isPlatformShared, setIsPlatformShared] = useState(app.access_level === 'platform')
  const [platformLoading, setPlatformLoading] = useState(false)

  async function loadShares() {
    try {
      const data = await listAppShares(app.name)
      setShares(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shares')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadShares()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.name])

  async function handlePlatformToggle() {
    setPlatformLoading(true)
    setError(null)

    try {
      if (isPlatformShared) {
        await unshareFromPlatform(app.name)
        setIsPlatformShared(false)
      } else {
        await shareWithPlatform(app.name)
        setIsPlatformShared(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update platform sharing')
    } finally {
      setPlatformLoading(false)
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return

    setAdding(true)
    setError(null)

    try {
      await shareApp(app.name, email, permission)
      setEmail('')
      await loadShares()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share app')
    } finally {
      setAdding(false)
    }
  }

  async function handleRemove(shareEmail: string) {
    try {
      await removeAppShare(app.name, shareEmail)
      await loadShares()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove share')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-md">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-1">Share "{app.display_name || app.name}"</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Control who can access your app
          </p>

          {/* Platform Sharing Toggle */}
          <div className="p-4 rounded-lg bg-secondary mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isPlatformShared ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">Share with all platform users</p>
                  <p className="text-xs text-muted-foreground">
                    {isPlatformShared ? 'Everyone on the platform can view this app' : 'Only you and invited users can access'}
                  </p>
                </div>
              </div>
{platformLoading ? (
                <div className="w-11 h-6 rounded-full bg-muted-foreground/30 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              ) : (
                <Switch
                  checked={isPlatformShared}
                  onCheckedChange={handlePlatformToggle}
                />
              )}
            </div>
          </div>

          <div className="border-t border-border pt-4 mb-4">
            <p className="text-sm font-medium mb-3">Share with specific people</p>

            {/* Add Share Form */}
            <form onSubmit={handleAdd} className="flex gap-2 mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <select
                value={permission}
                onChange={(e) => setPermission(e.target.value)}
                className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="view">View</option>
                <option value="edit">Edit</option>
              </select>
              <button
                type="submit"
                disabled={adding || !email}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
              </button>
            </form>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
              {error}
            </div>
          )}

          {/* Shares List */}
          <div className="space-y-2">
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : shares.length === 0 ? (
              <p className="text-center text-muted-foreground py-4 text-sm">
                No individual shares yet
              </p>
            ) : (
              shares.map((share) => (
                <div
                  key={share.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-secondary"
                >
                  <div>
                    <div className="font-medium text-sm">{share.email}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <span className="capitalize">{share.permission}</span>
                      {!share.user_registered && (
                        <span className="text-yellow-500">(pending invite)</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(share.email)}
                    className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
